/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { ContentCard } from '@repo/shared-client/components/blocks'
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useEventCategoryOptions } from '@web/src/features/event/use-event-category-options'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import { CircleXIcon, LayersIcon, LocateFixedIcon, NavigationIcon, UsersIcon } from 'lucide-react'
import type { MouseEvent } from 'react'
import SFDateCard from 'src/components/primatives/sf-date-card/sf-date-card'
import SFMediaImage from 'src/components/primatives/sf-media-image/sf-media-image'
import type {
  EventAttendanceStatus,
  EventCategoryEnum,
  EventVisibility,
  NSFWRatingEnum,
  ReviewEnum,
} from 'types/graphql'
import { getEventAttendanceStatusBadge } from '../utils/eventBadges'

export interface EventCardEvent {
  id: string
  slug?: string
  name: string
  category?: EventCategoryEnum
  tagLine?: string
  startDate?: string
  location?: string
  locationExact?: boolean
  admission?: boolean
  capacity?: number
  attendanceCounts?: {
    goingCount: number
  }
  distance?: number
  visibility: EventVisibility
  firstMedia?: {
    imageUrlMedium?: string
    reviewStatus?: ReviewEnum
    nsfwRating?: NSFWRatingEnum
  }
  tags?: {
    id: string
    name: string
  }[]
  myAttendance?: {
    id: string
    status: EventAttendanceStatus
  }
  groupId?: string
  groupName?: string
  groupSlug?: string
  groupCoverImageUrlSmall?: string
  groupCoverImageOffsetX?: number
  groupCoverImageOffsetY?: number
  groupCoverNsfwRating?: NSFWRatingEnum
  status?: string
  ownerId?: string
}

interface EventImageProps {
  event: EventCardEvent
}

const EventImage = (props: EventImageProps) => {
  return (
    <div
      className="relative h-full min-h-[inherit] w-full overflow-hidden bg-black transition-transform duration-[400ms] ease-out group-hover/content-card:scale-[1.06]"
      data-event-card-media=""
    >
      <SFMediaImage
        backgroundSize="contain"
        blurredBackdrop
        media={props.event.firstMedia}
        missingImage="/missing-event.webp"
      />
    </div>
  )
}

interface EventContentProps {
  event: EventCardEvent
  collapsed?: boolean
}

function EventCancelledHeader() {
  const { t } = useTranslation()

  return (
    <div className="flex w-full items-center justify-center gap-2 bg-red-600 py-1 text-white">
      <CircleXIcon className="size-5 shrink-0" />
      <span className="font-bold uppercase">{t('Event Cancelled')}</span>
    </div>
  )
}

const EventContent = (props: EventContentProps) => {
  const { t } = useTranslation()
  const { labelFor } = useEventCategoryOptions()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const { event } = props
  const locationName = event.locationExact ? event.location : t('Near {{location}}', { location: event.location ?? '' })

  const handleGroupNavigation = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (event.groupId) {
      navigate({ to: '/group/$groupIdOrSlug', params: { groupIdOrSlug: event.groupSlug || event.groupId } } as never)
    }
  }

  return (
    <div
      className={cn('flex h-full w-full flex-col items-start gap-0 px-0 py-1', props.collapsed && 'gap-0')}
      data-collapsed={props.collapsed ? 'true' : undefined}
    >
      <div className="flex w-full items-start justify-between">
        <Tooltip>
          <TooltipTrigger render={<span className="inline-block w-full cursor-default text-left align-top" />}>
            <span className="line-clamp-2 w-full break-words font-medium text-lg leading-snug tracking-wide">
              {event.name}
            </span>
          </TooltipTrigger>
          <TooltipContent>{event.name}</TooltipContent>
        </Tooltip>
      </div>

      {event.groupId && (
        <div className="flex w-full flex-wrap items-center gap-1 text-muted-foreground text-sm">
          <LayersIcon className="size-4 shrink-0" />
          <span className="shrink-0 whitespace-nowrap">{t('Event By:')}</span>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: Nested inside card Link; matches legacy Chakra Text */}
          {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: Nested inside card Link */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: Nested inside card Link */}
          <span
            className="min-w-0 max-w-full cursor-pointer truncate font-semibold text-primary text-sm hover:underline"
            onClick={handleGroupNavigation}
          >
            {event.groupName}
          </span>
        </div>
      )}

      <div className="flex max-h-[60px] w-full flex-wrap items-center justify-start gap-1 overflow-hidden py-1">
        {event.category && <Badge variant="outline">{labelFor(event.category)}</Badge>}
        {event.tags?.map((tag) => (
          <Badge key={tag.id} variant="outline">
            {tag.name}
          </Badge>
        ))}
      </div>

      {!props.collapsed && <p className="line-clamp-2 pb-1 text-muted-foreground">{event.tagLine}</p>}

      <div className="grow" />

      <div className="flex w-full flex-wrap items-center gap-3 pt-1 text-[0.85rem] text-muted-foreground">
        {isAuthenticated && event.distance != null && (
          <div className="flex items-center gap-1">
            <NavigationIcon className="size-4 shrink-0" />
            <span className="whitespace-nowrap">{t('{{distance}} km', { distance: event.distance.toFixed(0) })}</span>
          </div>
        )}
        {event.attendanceCounts != null && (
          <div className="flex items-center gap-1 text-[0.85rem]">
            <UsersIcon className="size-4 shrink-0" />
            <span>{event.attendanceCounts.goingCount || 0}</span>
          </div>
        )}
      </div>

      {isAuthenticated ? (
        <div
          className={cn(
            'flex w-full items-center gap-1 text-[0.85rem] text-muted-foreground',
            props.collapsed ? '' : 'pt-1'
          )}
        >
          <LocateFixedIcon className="size-4 shrink-0" />
          <Tooltip>
            <TooltipTrigger render={<span className="inline-block min-w-0 flex-1 cursor-default" />}>
              <span className="line-clamp-1">{locationName}</span>
            </TooltipTrigger>
            <TooltipContent>{locationName}</TooltipContent>
          </Tooltip>
        </div>
      ) : (
        <div className={cn('flex w-full flex-nowrap items-center gap-1 text-[0.85rem]', props.collapsed ? '' : 'pt-1')}>
          <LocateFixedIcon className="size-4 shrink-0 text-muted-foreground" />
          <span className="line-clamp-1 whitespace-nowrap text-muted-foreground text-sm">
            {t('Location Available To Members')}
          </span>
        </div>
      )}
    </div>
  )
}

function DateCardOverlay({ startDate }: { startDate?: string }) {
  if (!startDate) return null

  return (
    <div className="pointer-events-none absolute top-2 left-2 z-1">
      <SFDateCard date={startDate} size="sm" />
    </div>
  )
}

function GroupCoverOverlay({ event }: { event: EventCardEvent }) {
  const navigate = useNavigate()
  const { groupId, groupCoverImageUrlSmall } = event

  if (!(groupId && groupCoverImageUrlSmall)) return null

  const handleClick = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    navigate({ to: '/group/$groupIdOrSlug', params: { groupIdOrSlug: event.groupSlug || groupId } } as never)
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          // biome-ignore lint/a11y/noStaticElementInteractions: Card is wrapped in Link; span matches prior Box+Tooltip pattern
          // biome-ignore lint/a11y/noNoninteractiveElementInteractions: Card is wrapped in Link
          // biome-ignore lint/a11y/useKeyWithClickEvents: Card is wrapped in Link
          <span
            className="pointer-events-auto absolute top-2 right-2 z-1 inline-block cursor-pointer"
            onClick={handleClick}
          />
        }
      >
        <SFMediaImage
          backgroundPosition={`center ${event.groupCoverImageOffsetY || 50}%`}
          backgroundSize="cover"
          className="h-[55px] w-[55px] rounded-sm border border-black border-solid shadow-sm"
          media={{
            ownerId: event.ownerId,
            imageUrlMedium: event.groupCoverImageUrlSmall,
            nsfwRating: event.groupCoverNsfwRating,
          }}
          missingImage="/missing-group.webp"
        />
      </TooltipTrigger>
      <TooltipContent>{event.groupName}</TooltipContent>
    </Tooltip>
  )
}

export type EventCardLayoutMode = 'compact' | 'regular'

interface EventCardProps {
  cardLayout: EventCardLayoutMode
  event: EventCardEvent
}

const EventCard = (props: EventCardProps) => {
  const { event } = props
  const isCompact = props.cardLayout === 'compact'
  const isCancelled = event.status === 'CANCELLED'

  const renderBody = (collapsed: boolean) => <EventContent collapsed={collapsed} event={event} />

  const imageNode = <EventImage event={event} />

  const imageControls = (
    <>
      <DateCardOverlay startDate={event.startDate} />
      <GroupCoverOverlay event={event} />
      {!isCancelled && event.myAttendance?.status && (
        <div className="pointer-events-auto absolute right-2 bottom-2 z-1 flex">
          {getEventAttendanceStatusBadge(event.myAttendance.status, 'default')}
        </div>
      )}
    </>
  )

  return (
    <ContentCard
      bodyClassName="p-1 sm:p-2"
      bodyCollapsed={renderBody(true)}
      bodyExpanded={renderBody(false)}
      compactImageMinHeight={140}
      compactImageWidth={160}
      expandedImageHeight={180}
      header={isCancelled ? <EventCancelledHeader /> : undefined}
      image={imageNode}
      imageControls={imageControls}
      imageSide="left"
      layout={isCompact ? 'compact' : 'expanded'}
      params={{ eventIdOrSlug: event.slug || event.id } as never}
      to="/event/$eventIdOrSlug"
    />
  )
}

export default EventCard
